/**
 * Beautiful colored PDF export helper for the Umre Paneli.
 *
 * Different from `printView` in exportUtils.js — that one is a bare-bones
 * grayscale table print. This one produces a branded, gold+navy PDF-ready
 * document with:
 *
 *   • Ariaf letter-mark logo (drawn as inline SVG — no binary asset needed)
 *   • Islamic decorative crescent / star border ornaments
 *   • Colored table with alternating rows
 *   • Metadata banner (agency name + date + total)
 *   • Quranic verse (Al-Baqara 2:282) about honest commerce as a footer,
 *     Arabic + Turkish, per the user's request.
 *
 * The output is delivered by opening a new window with the styled HTML and
 * auto-invoking `window.print()`. Modern browsers offer "Save as PDF" in
 * that dialog so no client-side PDF library is needed (keeps bundle small,
 * fonts and Arabic ligatures render natively).
 */

// SVG letter-mark used in place of a bitmap logo — infinitely scalable and
// embeds cleanly into the print PDF without a network request.
const LOGO_SVG = `
<svg viewBox="0 0 220 60" xmlns="http://www.w3.org/2000/svg" width="180" height="50">
  <defs>
    <linearGradient id="lg" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0%" stop-color="#C9A24C"/>
      <stop offset="100%" stop-color="#8B6B2A"/>
    </linearGradient>
  </defs>
  <!-- 8-pointed Islamic star mark -->
  <g transform="translate(30,30)">
    <path d="M0,-22 L6,-6 L22,0 L6,6 L0,22 L-6,6 L-22,0 L-6,-6 Z" fill="url(#lg)"/>
    <circle cx="0" cy="0" r="6" fill="#1B2A5C"/>
  </g>
  <!-- Wordmark -->
  <text x="60" y="30" font-family="Georgia, serif" font-size="26" fill="#1B2A5C" font-weight="700" letter-spacing="1">ARIAF</text>
  <text x="60" y="48" font-family="Georgia, serif" font-size="10" fill="#8B6B2A" letter-spacing="4">UMRE PANELİ</text>
</svg>`;

// Corner ornament — a subtle crescent + 8-pointed star cluster in gold.
const CORNER_ORNAMENT = `
<svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" width="70" height="70">
  <g fill="none" stroke="#C9A24C" stroke-width="1.2" opacity="0.55">
    <path d="M60,20 A18,18 0 1,0 60,50 A14,14 0 1,1 60,20 Z" fill="#C9A24C" opacity="0.85"/>
    <circle cx="30" cy="60" r="3" fill="#C9A24C"/>
    <circle cx="20" cy="45" r="2" fill="#C9A24C"/>
    <circle cx="35" cy="50" r="1.5" fill="#C9A24C"/>
  </g>
</svg>`;

// Quranic verses about honest business dealings & Umrah. A random one is
// selected per PDF so the same document doesn't feel repetitive when an
// agency exports many in a row. Each entry has arabic + a Turkish meal +
// a short reference. Curated for both commerce (borç, ölçü) and ibadet
// (Umre, hac, ihram) themes since Ariaf sits at that intersection.
const AYETS = [
  {
    ar: "يَا أَيُّهَا الَّذِينَ آمَنُوا إِذَا تَدَايَنتُم بِدَيْنٍ إِلَىٰ أَجَلٍ مُّسَمًّى فَاكْتُبُوهُ",
    tr: "\"Ey iman edenler! Belirli bir süreye kadar birbirinize borçlandığınız zaman onu yazın.\"",
    ref: "— Bakara Sûresi, 282. Ayet (kısmi meal)",
  },
  {
    ar: "وَأَتِمُّوا الْحَجَّ وَالْعُمْرَةَ لِلَّهِ",
    tr: "\"Haccı ve umreyi Allah için tamamlayın.\"",
    ref: "— Bakara Sûresi, 196. Ayet (kısmi meal)",
  },
  {
    ar: "وَأَوْفُوا الْكَيْلَ إِذَا كِلْتُمْ وَزِنُوا بِالْقِسْطَاسِ الْمُسْتَقِيمِ",
    tr: "\"Ölçtüğünüz zaman ölçüyü tam yapın; doğru terazi ile tartın.\"",
    ref: "— İsrâ Sûresi, 35. Ayet (kısmi meal)",
  },
  {
    ar: "وَلَا تَبْخَسُوا النَّاسَ أَشْيَاءَهُمْ وَلَا تُفْسِدُوا فِي الْأَرْضِ",
    tr: "\"İnsanların hakkını eksik vermeyin; yeryüzünde bozgunculuk yapmayın.\"",
    ref: "— A'râf Sûresi, 85. Ayet (kısmi meal)",
  },
  {
    ar: "إِنَّ اللَّهَ يَأْمُرُ بِالْعَدْلِ وَالْإِحْسَانِ",
    tr: "\"Şüphesiz Allah, adaleti ve iyiliği emreder.\"",
    ref: "— Nahl Sûresi, 90. Ayet (kısmi meal)",
  },
  {
    ar: "يَا أَيُّهَا الَّذِينَ آمَنُوا أَوْفُوا بِالْعُقُودِ",
    tr: "\"Ey iman edenler! Sözleşmelerinizi eksiksiz yerine getirin.\"",
    ref: "— Mâide Sûresi, 1. Ayet (kısmi meal)",
  },
  {
    ar: "وَتَعَاوَنُوا عَلَى الْبِرِّ وَالتَّقْوَىٰ",
    tr: "\"İyilik ve takva üzere birbirinizle yardımlaşın.\"",
    ref: "— Mâide Sûresi, 2. Ayet (kısmi meal)",
  },
];

// Sabit hadîs — Umre'ye özel. Her PDF'in altında ayetin altında yer alır.
// Sahih Buhârî & Müslim'de geçen "iki umre arasındaki günahlara kefarettir"
// müjdesi. Umre acenteleri için özellikle mânâlı.
const HADIS = {
  ar: "الْعُمْرَةُ إِلَى الْعُمْرَةِ كَفَّارَةٌ لِمَا بَيْنَهُمَا وَالْحَجُّ الْمَبْرُورُ لَيْسَ لَهُ جَزَاءٌ إِلَّا الْجَنَّةُ",
  tr: "\"Umre, iki umre arasındaki günahlara kefarettir. Mebrûr bir haccın karşılığı ise ancak cennettir.\"",
  ref: "— Hz. Muhammed (s.a.v.), Buhârî & Müslim",
};

// Rasgele bir ayet seç — Math.random() PDF açıldığında farklı bir tane
// verir. Bir agency arka arkaya birden fazla PDF çıkarırsa her seferinde
// farklı bir dua/ayet görünür.
function pickAyet() {
  return AYETS[Math.floor(Math.random() * AYETS.length)];
}

function escapeHtml(v) {
  if (v === null || v === undefined) return "";
  return String(v)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Export data rows as a printable, branded PDF-ready document.
 *
 * @param {object} options
 * @param {string} options.title        Document title, e.g. "Rezervasyonlar"
 * @param {Array}  options.columns      [{key, label, value?}] column defs
 * @param {Array}  options.rows         data rows
 * @param {string} [options.subtitle]   optional sub-line under the title
 * @param {object} [options.meta]       optional key/value pairs shown in the banner
 *                                       e.g. { "Toplam Kayıt": 12, "Acente": "Demo" }
 * @param {string} [options.footerNote] optional extra note above the ayet
 */
export function exportBrandedPdf({ title, columns, rows, subtitle = "", meta = {}, footerNote = "" }) {
  const w = window.open("", "_blank", "width=1100,height=900");
  if (!w) {
    // Popup blocker — surface a clear hint to the user.
    // eslint-disable-next-line no-alert
    alert("PDF çıktısı açılamadı. Lütfen tarayıcınızda pop-up engelleyiciyi devre dışı bırakın ve tekrar deneyin.");
    return;
  }

  const ayet = pickAyet();

  const now = new Date();
  const dateStr = now.toLocaleString("tr-TR", {
    day: "2-digit", month: "long", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });

  const head = columns.map((c) => `<th>${escapeHtml(c.label)}</th>`).join("");
  const body = rows
    .map((r, i) => {
      const cells = columns
        .map((c) => {
          const raw = typeof c.value === "function" ? c.value(r) : r[c.key];
          return `<td class="${c.align === "right" ? "r" : ""}">${escapeHtml(raw ?? "")}</td>`;
        })
        .join("");
      return `<tr class="${i % 2 ? "even" : "odd"}">${cells}</tr>`;
    })
    .join("");

  const metaRows = Object.entries(meta)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(
      ([k, v]) =>
        `<div class="meta-cell"><div class="meta-k">${escapeHtml(k)}</div><div class="meta-v">${escapeHtml(v)}</div></div>`
    )
    .join("");

  const styles = `
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; background: #FFFDF8; }
    body { font-family: "Georgia", "Cambria", "Times New Roman", serif; color: #1B2A5C; }
    .page {
      max-width: 900px; margin: 0 auto; padding: 28px 36px 36px;
      background: #FFFDF8;
      position: relative;
    }
    /* Corner ornaments in the four corners of the page */
    .corner { position: absolute; opacity: 0.85; }
    .corner.tl { top: 8px; left: 8px; transform: rotate(0deg); }
    .corner.tr { top: 8px; right: 8px; transform: scaleX(-1); }
    .corner.bl { bottom: 8px; left: 8px; transform: scaleY(-1); }
    .corner.br { bottom: 8px; right: 8px; transform: scale(-1,-1); }

    .header {
      display: flex; align-items: center; justify-content: space-between;
      border-bottom: 3px double #C9A24C;
      padding-bottom: 14px; margin-bottom: 18px;
      gap: 20px;
    }
    .header .brand-block { display: flex; align-items: center; gap: 12px; }
    .header .doc-title { text-align: right; }
    .header h1 {
      margin: 0; font-size: 22px; letter-spacing: 1.5px;
      color: #1B2A5C;
    }
    .header .sub {
      font-size: 11px; color: #8B6B2A; letter-spacing: 3px;
      text-transform: uppercase; margin-top: 4px;
    }
    .header .date { font-size: 10px; color: #64748B; margin-top: 6px; }

    /* Metadata banner */
    .meta-banner {
      display: flex; flex-wrap: wrap; gap: 12px 22px;
      background: linear-gradient(135deg, #1B2A5C 0%, #2A3E7A 100%);
      color: #F7F1DE; padding: 14px 18px; border-radius: 10px;
      margin-bottom: 20px;
    }
    .meta-cell { min-width: 130px; }
    .meta-k { font-size: 9px; letter-spacing: 2px; text-transform: uppercase; color: #C9A24C; }
    .meta-v { font-size: 14px; margin-top: 3px; font-weight: 600; }

    /* Table */
    table {
      width: 100%; border-collapse: collapse; font-size: 11px;
      background: #FFFFFF; border-radius: 8px; overflow: hidden;
      box-shadow: 0 1px 3px rgba(27,42,92,0.08);
    }
    thead th {
      background: linear-gradient(135deg, #C9A24C 0%, #8B6B2A 100%);
      color: #FFFDF8; text-align: left; padding: 9px 10px;
      font-size: 10px; letter-spacing: 1.2px; text-transform: uppercase;
      font-family: "Georgia", serif;
    }
    tbody td {
      padding: 8px 10px; border-bottom: 1px solid #F1E9CF; color: #1B2A5C;
    }
    tbody tr.even td { background: #FBF6E8; }
    tbody tr.odd  td { background: #FFFFFF; }
    tbody tr:last-child td { border-bottom: none; }
    td.r, th.r { text-align: right; }

    /* Footer with ayet */
    .footer-note {
      margin-top: 22px; padding: 14px 18px;
      background: #F7F1DE; border-left: 4px solid #C9A24C;
      color: #4B3B10; font-size: 12px; border-radius: 6px;
    }
    .ayet-box, .hadis-box {
      margin-top: 20px; padding: 22px 24px;
      border-radius: 12px; text-align: center;
      position: relative; overflow: hidden;
    }
    .ayet-box {
      background: linear-gradient(135deg, #1B2A5C 0%, #2A3E7A 60%, #1B2A5C 100%);
      color: #FFFDF8;
    }
    .hadis-box {
      background: linear-gradient(135deg, #4B3B10 0%, #6B5220 60%, #4B3B10 100%);
      color: #FFFDF8;
    }
    .ayet-box::before,
    .ayet-box::after,
    .hadis-box::before,
    .hadis-box::after {
      content: "❋"; color: #C9A24C; font-size: 18px;
      position: absolute; opacity: 0.8;
    }
    .ayet-box::before,   .hadis-box::before { top: 8px; left: 14px; }
    .ayet-box::after,    .hadis-box::after  { bottom: 8px; right: 14px; }
    .box-label {
      font-size: 10px; letter-spacing: 4px; text-transform: uppercase;
      color: #C9A24C; margin-bottom: 8px; font-family: Georgia, serif;
    }
    .ar-text {
      font-family: "Amiri", "Scheherazade New", "Traditional Arabic", "Times New Roman", serif;
      font-size: 20px; line-height: 1.8; direction: rtl; unicode-bidi: bidi-override;
      color: #F7E7B5; margin-bottom: 10px;
    }
    .tr-text {
      font-size: 13px; font-style: italic; color: #FFFDF8;
      margin-bottom: 6px;
    }
    .ref-text { font-size: 10px; color: #C9A24C; letter-spacing: 2px; text-transform: uppercase; }

    .page-footer {
      margin-top: 14px; text-align: center;
      font-size: 10px; color: #94A3B8; letter-spacing: 2px;
    }

    /* Actions bar (hidden in print) */
    .actions {
      position: fixed; top: 12px; right: 12px;
      display: flex; gap: 8px; z-index: 10;
    }
    .actions button {
      background: #1B2A5C; color: #FFFDF8; border: 0; padding: 8px 16px;
      border-radius: 20px; font-family: inherit; font-size: 12px;
      cursor: pointer; box-shadow: 0 2px 6px rgba(0,0,0,0.15);
    }
    .actions button.gold { background: #C9A24C; }

    @page { size: A4 landscape; margin: 12mm; }
    @media print {
      .actions { display: none !important; }
      .page { max-width: none; padding: 0; }
      body { background: #FFFDF8 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  `;

  const html = `<!doctype html><html lang="tr"><head>
    <meta charset="utf-8"/>
    <title>${escapeHtml(title)} — Ariaf ASIS Acente Paneli</title>
    <style>${styles}</style>
  </head><body>
    <div class="actions">
      <button class="gold" onclick="window.print()">PDF Olarak Kaydet</button>
      <button onclick="window.print()">Yazdır</button>
      <button onclick="window.close()">Kapat</button>
    </div>
    <div class="page">
      <div class="corner tl">${CORNER_ORNAMENT}</div>
      <div class="corner tr">${CORNER_ORNAMENT}</div>
      <div class="corner bl">${CORNER_ORNAMENT}</div>
      <div class="corner br">${CORNER_ORNAMENT}</div>

      <header class="header">
        <div class="brand-block">${LOGO_SVG}</div>
        <div class="doc-title">
          <h1>${escapeHtml(title)}</h1>
          ${subtitle ? `<div class="sub">${escapeHtml(subtitle)}</div>` : ""}
          <div class="date">${escapeHtml(dateStr)}</div>
        </div>
      </header>

      ${metaRows ? `<div class="meta-banner">${metaRows}</div>` : ""}

      <table>
        <thead><tr>${head}</tr></thead>
        <tbody>${body || `<tr><td colspan="${columns.length}" style="text-align:center;padding:26px;color:#94A3B8">Görüntülenecek kayıt bulunamadı</td></tr>`}</tbody>
      </table>

      ${footerNote ? `<div class="footer-note">${escapeHtml(footerNote)}</div>` : ""}

      <div class="ayet-box">
        <div class="box-label">Âyet-i Kerîme</div>
        <div class="ar-text">${ayet.ar}</div>
        <div class="tr-text">${escapeHtml(ayet.tr)}</div>
        <div class="ref-text">${escapeHtml(ayet.ref)}</div>
      </div>

      <div class="hadis-box">
        <div class="box-label">Hadîs-i Şerîf</div>
        <div class="ar-text">${HADIS.ar}</div>
        <div class="tr-text">${escapeHtml(HADIS.tr)}</div>
        <div class="ref-text">${escapeHtml(HADIS.ref)}</div>
      </div>

      <div class="page-footer">ARIAF UMRE PANELİ · KATERİNG &amp; REZERVASYON SİSTEMİ</div>
    </div>
    <script>
      // Auto-open the print dialog once fonts settle — the user can either
      // save as PDF from there or dismiss the dialog to review the doc first.
      window.addEventListener('load', function(){
        setTimeout(function(){ try { window.print(); } catch(_){} }, 450);
      });
    <\/script>
  </body></html>`;

  w.document.open();
  w.document.write(html);
  w.document.close();
}

/**
 * Convenience wrapper: exports a full HTML block as a beautiful branded PDF.
 * Useful when a page already has a nicely formatted print layout (e.g., group
 * program template, reservation detail).
 *
 * @param {object} options
 * @param {string} options.title
 * @param {string} options.bodyHtml   HTML string of the actual document body
 * @param {string} [options.subtitle]
 */
export function exportHtmlPdf({ title, bodyHtml, subtitle = "" }) {
  const w = window.open("", "_blank", "width=1100,height=900");
  if (!w) {
    // eslint-disable-next-line no-alert
    alert("PDF çıktısı açılamadı. Lütfen tarayıcınızda pop-up engelleyiciyi devre dışı bırakın ve tekrar deneyin.");
    return;
  }
  const ayet = pickAyet();
  const now = new Date();
  const dateStr = now.toLocaleString("tr-TR", {
    day: "2-digit", month: "long", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
  const styles = `
    html, body { margin: 0; padding: 0; background: #FFFDF8; font-family: Georgia, serif; color: #1B2A5C; }
    .page { max-width: 900px; margin: 0 auto; padding: 28px 36px 36px; }
    .header { display: flex; align-items: center; justify-content: space-between;
      border-bottom: 3px double #C9A24C; padding-bottom: 14px; margin-bottom: 18px; }
    .header h1 { margin: 0; font-size: 22px; letter-spacing: 1.5px; color: #1B2A5C; }
    .header .sub { font-size: 11px; color: #8B6B2A; letter-spacing: 3px; text-transform: uppercase; margin-top: 4px; }
    .header .date { font-size: 10px; color: #64748B; margin-top: 6px; }
    .body { margin-bottom: 24px; }
    .ayet-box, .hadis-box { margin-top: 20px; padding: 22px 24px;
      border-radius: 12px; text-align: center; }
    .ayet-box { background: linear-gradient(135deg, #1B2A5C 0%, #2A3E7A 60%, #1B2A5C 100%); color: #FFFDF8; }
    .hadis-box { background: linear-gradient(135deg, #4B3B10 0%, #6B5220 60%, #4B3B10 100%); color: #FFFDF8; }
    .box-label { font-size: 10px; letter-spacing: 4px; text-transform: uppercase;
      color: #C9A24C; margin-bottom: 8px; font-family: Georgia, serif; }
    .ar-text { font-family: "Amiri", "Scheherazade New", "Traditional Arabic", serif;
      font-size: 20px; line-height: 1.8; direction: rtl; color: #F7E7B5; margin-bottom: 10px; }
    .tr-text { font-size: 13px; font-style: italic; color: #FFFDF8; margin-bottom: 6px; }
    .ref-text { font-size: 10px; color: #C9A24C; letter-spacing: 2px; text-transform: uppercase; }
    .actions { position: fixed; top: 12px; right: 12px; display: flex; gap: 8px; z-index: 10; }
    .actions button { background: #1B2A5C; color: #FFFDF8; border: 0; padding: 8px 16px;
      border-radius: 20px; font-family: inherit; font-size: 12px; cursor: pointer; }
    .actions button.gold { background: #C9A24C; }
    @page { size: A4; margin: 12mm; }
    @media print {
      .actions { display: none !important; }
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  `;
  const html = `<!doctype html><html lang="tr"><head><meta charset="utf-8"/>
    <title>${escapeHtml(title)} — Ariaf</title><style>${styles}</style></head><body>
    <div class="actions">
      <button class="gold" onclick="window.print()">PDF Olarak Kaydet</button>
      <button onclick="window.print()">Yazdır</button>
      <button onclick="window.close()">Kapat</button>
    </div>
    <div class="page">
      <header class="header">
        <div>${LOGO_SVG}</div>
        <div style="text-align:right">
          <h1>${escapeHtml(title)}</h1>
          ${subtitle ? `<div class="sub">${escapeHtml(subtitle)}</div>` : ""}
          <div class="date">${escapeHtml(dateStr)}</div>
        </div>
      </header>
      <div class="body">${bodyHtml}</div>
      <div class="ayet-box">
        <div class="box-label">Âyet-i Kerîme</div>
        <div class="ar-text">${ayet.ar}</div>
        <div class="tr-text">${escapeHtml(ayet.tr)}</div>
        <div class="ref-text">${escapeHtml(ayet.ref)}</div>
      </div>
      <div class="hadis-box">
        <div class="box-label">Hadîs-i Şerîf</div>
        <div class="ar-text">${HADIS.ar}</div>
        <div class="tr-text">${escapeHtml(HADIS.tr)}</div>
        <div class="ref-text">${escapeHtml(HADIS.ref)}</div>
      </div>
    </div>
    <script>window.addEventListener('load', function(){ setTimeout(function(){ try { window.print(); } catch(_){} }, 450); });<\/script>
  </body></html>`;
  w.document.open();
  w.document.write(html);
  w.document.close();
}
